import React, { useState, useEffect, useMemo, useCallback, useRef, createContext, useContext } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, Handle, Position,
  useNodesState, useEdgesState, BaseEdge, EdgeLabelRenderer, getBezierPath, getStraightPath,
  MarkerType, useStore, useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useAuth } from "../authentication/AuthContext";
import { SideDrawer } from "./BuilderUtils";
import PageComposer from "./PageComposer";
import { displayInfoOf } from "./pageKinds";
import DecisionEditor from "./DecisionEditor";
import {
  buildGraph, computeSharedUsage, collectSubtree,
  handleForDecision, parseHandle,
  PAGE_HANDLE, SEQ_SOURCE_HANDLE, SEQ_TARGET_HANDLE, CONS_TARGET_HANDLE,
} from "./graphBuilder";
import { layoutGraph, visibleRowsFor, isExpandable, NODE_W, COL_W, GROUP_PREVIEW_ITEMS } from "./graphLayout";
import * as Draft from "./graphDraft";
import {
  ConditionPopover, PagePickerPopover, CommandPalette,
  FindingsPanel, ChangesBar, SaveDialog,
} from "./GraphPopovers";
import "../../builder.css";

const EDGE_COLORS = {
  sequence: "#94a3b8",
  "decision-taken": "#16a34a",
  "decision-not-taken": "#dc2626",
  "page-consequence": "#3b82f6",
  group: "#94a3b8",
};

const EDGE_LEGEND = [
  { kind: "sequence", label: "Ordre de la pile" },
  { kind: "decision-taken", label: "Si décision prise" },
  { kind: "decision-not-taken", label: "Si décision non prise" },
  { kind: "page-consequence", label: "Conséquence générale de page" },
];

/* Actions passées aux nœuds par contexte plutôt que par `data` : garder la
   référence de `data` stable évite de re-rendre 100 cartes à chaque survol. */
const GraphActions = createContext({});

/* ─── Briques partagées entre vue compacte et vue dépliée ─────────── */

function DecisionPorts({ row, size = "sm" }) {
  return (
    <span className={`graph-ports ${size}`}>
      <span
        className={`graph-port-wrap ${row.takenCount ? "active" : ""}`}
        title="Si la mesure est prise — tirer pour ajouter une conséquence"
      >
        <span className="graph-port-glyph taken">✓</span>
        <Handle type="source" position={Position.Right} id={handleForDecision(row.decisionId, "taken")} className="graph-port" />
        {row.takenCount > 1 && <span className="graph-port-count">{row.takenCount}</span>}
      </span>
      <span
        className={`graph-port-wrap ${row.notTakenCount ? "active" : ""}`}
        title="Si la mesure n'est PAS prise — tirer pour ajouter une conséquence"
      >
        <span className="graph-port-glyph nottaken">✗</span>
        <Handle type="source" position={Position.Right} id={handleForDecision(row.decisionId, "nottaken")} className="graph-port" />
        {row.notTakenCount > 1 && <span className="graph-port-count">{row.notTakenCount}</span>}
      </span>
    </span>
  );
}

function CardToolbar({ node, expandable }) {
  const actions = useContext(GraphActions);
  return (
    <div className="graph-card-toolbar nodrag">
      {expandable && (
        <button
          className="graph-card-tool"
          onClick={(e) => { e.stopPropagation(); actions.onToggleCard(node.id); }}
          title={node.expanded ? "Replier la carte" : "Déplier : mesures et conséquences en tableau"}
        >{node.expanded ? "⤡" : "⤢"}</button>
      )}
      <button
        className="graph-card-tool"
        onClick={(e) => { e.stopPropagation(); actions.onOpenPage(node.pageId); }}
        title="Ouvrir la page — aperçu et édition"
      >👁</button>
      {node.inPile && (
        <button
          className="graph-card-tool danger"
          onClick={(e) => { e.stopPropagation(); actions.onRemoveFromPile(node.pileIndex, node.pageId); }}
          title="Retirer cette étape de la pile"
        >✕</button>
      )}
    </div>
  );
}

/* ─── Nœud : carte de page ────────────────────────────────────────── */
function PageNode({ id, data }) {
  const { node, findings, usage } = data;
  const actions = useContext(GraphActions);
  // Zoom sémantique : on ne s'abonne qu'au NIVEAU, pas à l'échelle continue,
  // pour ne re-rendre qu'aux trois paliers.
  const detail = useStore((s) => (s.transform[2] < 0.5 ? 0 : s.transform[2] < 1.05 ? 1 : 2));

  const anchors = (
    <>
      {/* Les ancres de pile ne sont pas connectables : l'ordre de la pile se
          modifie en déplaçant la carte ou via le « + » du lien, pas en tirant
          un trait. Seule l'ancre gauche accepte une conséquence entrante. */}
      <Handle type="target" position={Position.Top} id={SEQ_TARGET_HANDLE} className="graph-anchor seq" isConnectable={false} />
      <Handle type="source" position={Position.Bottom} id={SEQ_SOURCE_HANDLE} className="graph-anchor seq" isConnectable={false} />
      <Handle type="target" position={Position.Left} id={CONS_TARGET_HANDLE} className="graph-anchor cons" />
      <Handle type="source" position={Position.Right} id={PAGE_HANDLE} className="graph-anchor page" />
    </>
  );

  if (node.missing) {
    return (
      <div className="graph-node graph-node-missing">
        {anchors}
        <div className="graph-node-header">⚠ Page introuvable</div>
        <div className="graph-node-id">{node.pageId}</div>
      </div>
    );
  }

  const page = node.page;
  const typeInfo = displayInfoOf(page);
  const expandable = isExpandable(node);
  const worstSeverity = findings.page.some((f) => f.severity === "error")
    ? "error"
    : findings.page.some((f) => f.severity === "warning") ? "warning" : null;

  const header = (
    <>
      <div className="graph-node-header">
        {node.inPile && <span className="graph-node-step">{(node.pileIndex ?? 0) + 1}</span>}
        <span className="graph-node-icon">{typeInfo?.icon || "📄"}</span>
        <span className="graph-node-title">{page.title || page._id}</span>
        {worstSeverity && (
          <span
            className={`graph-node-flag nodrag sev-${worstSeverity}`}
            title={findings.page.map((f) => f.message).join("\n")}
          >
            {worstSeverity === "error" ? "⛔" : "⚠"}
          </span>
        )}
      </div>
      <div className="graph-node-meta">
        <span className="builder-tag tag-type">{page.type}</span>
        {!node.inPile && <span className="builder-tag graph-annex-badge" title="Atteinte uniquement par conséquence, absente de la pile">hors pile</span>}
        {node.isPool && (
          <span
            className="builder-tag graph-pool-badge"
            title="Cette page tire ses mesures dans le réservoir de l'atelier : les mesures montrées ici sont toutes les candidates, pas celles d'une séance donnée."
          >tirage</span>
        )}
        {detail > 0 && usage.page.length > 1 && (
          <span className="builder-tag graph-shared-badge" title={`Page utilisée par : ${usage.page.join(", ")}`}>
            🔗 {usage.page.length}
          </span>
        )}
        {expandable && detail > 0 && (
          <span className="graph-node-count">◆ {node.rows.length}</span>
        )}
      </div>
      <CardToolbar node={node} expandable={expandable} />
    </>
  );

  const className = [
    "graph-node",
    node.inPile ? "graph-node-spine" : "graph-node-annex",
    node.expanded ? "graph-node-expanded" : "",
    !node.hasDecisions && node.outgoingCount === 0 && !node.inPile ? "graph-node-chip" : "",
    worstSeverity ? `graph-node-flag-${worstSeverity}` : "",
  ].filter(Boolean).join(" ");

  /* ── Vue dépliée : mesures en colonnes, conséquences alignées dessous ──
     L'attribution se lit par la position de la colonne. Aucune arête n'est
     tracée pour les conséquences terminales : elles sont DANS la carte. */
  if (node.expanded && expandable && detail > 0) {
    const columns = `repeat(${node.rows.length}, ${COL_W}px)`;
    return (
      <div className={className} style={{ borderLeftColor: typeInfo?.color || "#94a3b8" }}>
        {anchors}
        {header}

        <div className="graph-exp-heads" style={{ gridTemplateColumns: columns }}>
          {node.rows.map((row) => {
            const rowFindings = findings.decisions[row.decisionId] || [];
            const rowFlag = rowFindings.some((f) => f.severity === "error") ? "error"
              : rowFindings.some((f) => f.severity === "warning") ? "warning" : null;
            return (
              <div
                key={row.decisionId}
                className={`graph-exp-head ${row.missing ? "missing" : ""}`}
                onMouseEnter={() => actions.onHoverRow(id, row.decisionId)}
                onMouseLeave={() => actions.onHoverRow(null, null)}
              >
                <span
                  className="graph-exp-head-title nodrag"
                  onClick={(e) => { e.stopPropagation(); actions.onOpenDecision(row.decisionId, row.title); }}
                  title={row.title}
                >
                  {row.missing && "⚠ "}{row.title}
                </span>
                <div className="graph-exp-head-ports">
                  {rowFlag && <span className={`graph-row-flag sev-${rowFlag}`} title={rowFindings.map((f) => f.message).join("\n")}>
                    {rowFlag === "error" ? "⛔" : "⚠"}
                  </span>}
                  <DecisionPorts row={row} size="lg" />
                </div>
              </div>
            );
          })}
        </div>

        <div className="graph-exp-label">Conséquences</div>
        <div className="graph-exp-cells" style={{ gridTemplateColumns: columns }}>
          {node.rows.map((row) => {
            const items = [
              ...row.taken.map((t) => ({ ...t, side: "taken" })),
              ...row.notTaken.map((t) => ({ ...t, side: "nottaken" })),
            ];
            return (
              <div
                key={row.decisionId}
                className="graph-exp-cell"
                onMouseEnter={() => actions.onHoverRow(id, row.decisionId)}
                onMouseLeave={() => actions.onHoverRow(null, null)}
              >
                {items.length === 0 && <span className="graph-exp-empty">—</span>}
                {items.map((item) => (
                  <button
                    key={item.edgeId}
                    type="button"
                    className={`graph-exp-item nodrag ${item.side} ${item.missing ? "missing" : ""}`}
                    onClick={(e) => { e.stopPropagation(); actions.onEditConsequence(item, { x: e.clientX, y: e.clientY }); }}
                    title={`${item.side === "taken" ? "Si prise" : "Si non prise"} · ${item.label}${item.chained ? " · enchaîne sur d'autres pages" : ""}`}
                  >
                    <span className="graph-exp-item-side">{item.side === "taken" ? "✓" : "✗"}</span>
                    <span className="graph-exp-item-title">{item.missing ? `⚠ ${item.targetPageId}` : item.targetTitle}</span>
                    {item.conditional && <span className="graph-exp-item-cond" title={item.label}>◆</span>}
                    {item.chained && <span className="graph-exp-item-chain">↗</span>}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  /* ── Vue compacte ── */
  const { rows, inertCount } = visibleRowsFor(node, false);
  const showRows = detail > 0 && node.hasDecisions;

  return (
    <div className={className} style={{ borderLeftColor: typeInfo?.color || "#94a3b8" }}>
      {anchors}
      {header}

      {showRows && (
        <ul className="graph-rows">
          {rows.map((row) => {
            const rowFindings = findings.decisions[row.decisionId] || [];
            const rowFlag = rowFindings.some((f) => f.severity === "error") ? "error"
              : rowFindings.some((f) => f.severity === "warning") ? "warning" : null;
            const shared = (usage.decisions[row.decisionId] || []).length;
            return (
              <li
                key={row.decisionId}
                className={`graph-row ${row.missing ? "missing" : ""}`}
                onMouseEnter={() => actions.onHoverRow(id, row.decisionId)}
                onMouseLeave={() => actions.onHoverRow(null, null)}
              >
                <span
                  className="graph-row-title nodrag"
                  onClick={(e) => { e.stopPropagation(); actions.onOpenDecision(row.decisionId, row.title); }}
                  title={row.missing ? "Décision introuvable en base" : row.title}
                >
                  {row.missing && "⚠ "}{row.title}
                </span>
                {detail > 1 && shared > 1 && (
                  <span className="graph-row-shared" title={`Mesure utilisée par ${shared} ateliers`}>🔗{shared}</span>
                )}
                {rowFlag && (
                  <span className={`graph-row-flag sev-${rowFlag}`} title={rowFindings.map((f) => f.message).join("\n")}>
                    {rowFlag === "error" ? "⛔" : "⚠"}
                  </span>
                )}
                <DecisionPorts row={row} />
              </li>
            );
          })}

          {inertCount > 0 && (
            <li
              className="graph-row-inert nodrag"
              onClick={(e) => { e.stopPropagation(); actions.onToggleCard(id); }}
              title="Déplier la carte : toutes les mesures et leurs conséquences"
            >
              ⋯ {inertCount} mesure{inertCount > 1 ? "s" : ""} sans conséquence — déplier ⤢
            </li>
          )}
        </ul>
      )}

      {detail === 0 && node.hasDecisions && (
        <div className="graph-node-summary">
          ◆ {node.rows.length} mesure{node.rows.length > 1 ? "s" : ""} · {node.outgoingCount} branche{node.outgoingCount > 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
}

/* ─── Nœud : groupe de conséquences terminales replié ─────────────── */
function GroupNode({ data }) {
  const { node } = data;
  const actions = useContext(GraphActions);
  const preview = node.items.slice(0, GROUP_PREVIEW_ITEMS);

  return (
    <div className="graph-group" onClick={() => actions.onToggleGroup(node.id)}>
      <Handle type="target" position={Position.Left} id={CONS_TARGET_HANDLE} className="graph-anchor cons" />
      <div className="graph-group-header">
        📎 {node.items.length} conséquences <span className="graph-group-chevron">déplier ▾</span>
      </div>
      <ul className="graph-group-items">
        {preview.map(({ edge, target }) => (
          <li key={edge.id} title={`${edge.decisionLabel || "Page"} → ${target?.page?.title || target?.pageId}`}>
            <span className={`graph-group-side ${edge.kind}`}>
              {edge.kind === "decision-taken" ? "✓" : edge.kind === "decision-not-taken" ? "✗" : "▸"}
            </span>
            {target?.page?.title || target?.pageId}
          </li>
        ))}
        {node.items.length > GROUP_PREVIEW_ITEMS && (
          <li className="graph-group-more">+ {node.items.length - GROUP_PREVIEW_ITEMS} autres…</li>
        )}
      </ul>
    </div>
  );
}

/* ─── Arêtes ──────────────────────────────────────────────────────── */

/* Le tronc : trait vertical net, avec le « + » d'insertion posé dessus.
   C'est le lien lui-même qui porte l'action « insérer une étape ici » —
   la position dans la pile est évidente, et la carte reste débarrassée. */
function SequenceEdge({ id, sourceX, sourceY, targetX, targetY, data, markerEnd }) {
  const actions = useContext(GraphActions);
  const [edgePath, labelX, labelY] = getStraightPath({ sourceX, sourceY, targetX, targetY });

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={{ stroke: EDGE_COLORS.sequence, strokeWidth: 2 }} />
      <EdgeLabelRenderer>
        <button
          type="button"
          className="graph-edge-insert nodrag nopan"
          style={{ transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)` }}
          onClick={(e) => { e.stopPropagation(); actions.onInsertAt(data.insertAtIndex, { x: e.clientX, y: e.clientY }); }}
          title="Insérer une étape ici"
        >+</button>
      </EdgeLabelRenderer>
    </>
  );
}

function ConsequenceEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, markerEnd, selected }) {
  const actions = useContext(GraphActions);
  const [edgePath, labelX, labelY] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  const color = data.missing ? "#dc2626" : EDGE_COLORS[data.kind] || "#94a3b8";

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: color,
          strokeWidth: selected ? 3.5 : data.conditional ? 2.5 : 1.75,
          strokeDasharray: data.conditional ? "5 4" : undefined,
        }}
      />
      {data.kind === "group" && (
        <EdgeLabelRenderer>
          <div className="graph-edge-pill" style={{ transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)` }}>
            {data.groupSize}
          </div>
        </EdgeLabelRenderer>
      )}
      {data.label && data.kind !== "group" && (
        <EdgeLabelRenderer>
          <div
            className={`graph-edge-label nodrag ${data.conditional ? "conditional" : ""}`}
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              borderColor: color, color,
            }}
            title={data.decisionLabel ? `${data.decisionLabel} — ${data.label}` : data.label}
            onClick={(e) => { e.stopPropagation(); actions.onEditEdge(data, { x: e.clientX, y: e.clientY }); }}
          >
            {data.decisionLabel && <strong>{data.decisionLabel}</strong>}
            <span>{data.label}</span>
            {data.variationDriven && <span className="graph-edge-label-variation">variante atelier</span>}
            {data.missing && <span className="graph-edge-label-warning">⚠ page manquante</span>}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const nodeTypes = { page: PageNode, group: GroupNode };
const edgeTypes = { consequence: ConsequenceEdge, sequence: SequenceEdge };

/* ─── Vue principale ──────────────────────────────────────────────── */
function WorkshopGraphInner() {
  const { configId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const { setCenter } = useReactFlow();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [baseConfig, setBaseConfig] = useState(location.state?.config || null);
  const [basePages, setBasePages] = useState([]);
  const [baseDecisions, setBaseDecisions] = useState([]);
  const [allConfigs, setAllConfigs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [draft, setDraft] = useState(Draft.emptyDraft);
  // Miroir du brouillon + historique tenus dans des refs : les updaters passés
  // à setState doivent rester purs (StrictMode les rejoue), on ne peut donc pas
  // empiler l'historique à l'intérieur.
  const draftRef = useRef(Draft.emptyDraft);
  const historyRef = useRef([]);

  const [drawerStack, setDrawerStack] = useState([]);
  const [composerPageId, setComposerPageId] = useState(null);
  const [hiddenKinds, setHiddenKinds] = useState(new Set());

  const [expandedCards, setExpandedCards] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [manualPositions, setManualPositions] = useState({});
  const [layoutNonce, setLayoutNonce] = useState(0);

  const [hoverRow, setHoverRow] = useState(null);      // { nodeId, decisionId }
  const [pulseNodeId, setPulseNodeId] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const [findings, setFindings] = useState(null);
  const [validating, setValidating] = useState(false);

  const [popover, setPopover] = useState(null);
  const [saveState, setSaveState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const apiGet = useCallback(async (path) => {
    const res = await fetch(`${apiUrl}${path}`, { headers: { Authorization: "Bearer " + token } });
    return res.json();
  }, [token, apiUrl]);

  /* ── Chargement ─────────────────────────────────────────────── */
  const reload = useCallback(() => Promise.all([
    apiGet("/get_all_pages"),
    apiGet("/get_all_decisions"),
    apiGet(`/get_game_config/${configId}`),
    apiGet("/get_all_game_configs"),
  ]).then(([pages, decisions, cfg, configs]) => {
    setBasePages(Array.isArray(pages) ? pages : []);
    setBaseDecisions(Array.isArray(decisions) ? decisions : []);
    setBaseConfig(cfg);
    setAllConfigs(Array.isArray(configs) ? configs : []);
    setLoading(false);
  }), [apiGet, configId]);

  useEffect(() => { reload(); }, [reload]);

  /* ── Préférences de vue persistées (localStorage, pas de schéma DB) ── */
  const prefsKey = `graphPrefs:${configId}`;
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(prefsKey) || "{}");
      if (saved.manualPositions) setManualPositions(saved.manualPositions);
      if (saved.expandedGroups) setExpandedGroups(new Set(saved.expandedGroups));
      if (saved.expandedCards) setExpandedCards(new Set(saved.expandedCards));
    } catch { /* préférences illisibles : on repart d'une vue propre */ }
  }, [prefsKey]);

  useEffect(() => {
    try {
      localStorage.setItem(prefsKey, JSON.stringify({
        manualPositions,
        expandedGroups: Array.from(expandedGroups),
        expandedCards: Array.from(expandedCards),
      }));
    } catch { /* quota plein : sans gravité, la vue se recalcule */ }
  }, [prefsKey, manualPositions, expandedGroups, expandedCards]);

  /* ── État effectif (base + brouillon) ───────────────────────── */
  const config = useMemo(() => Draft.effectiveConfig(draft, baseConfig), [draft, baseConfig]);
  const pages = useMemo(() => Draft.effectivePages(draft, basePages), [draft, basePages]);
  const decisions = useMemo(() => Draft.effectiveDecisions(draft, baseDecisions), [draft, baseDecisions]);

  const pagesById = useMemo(() => new Map(pages.map((p) => [p._id, p])), [pages]);
  const decisionsById = useMemo(() => new Map(decisions.map((d) => [d._id, d])), [decisions]);

  const base = useMemo(() => ({
    config: baseConfig,
    pagesById,
    decisionsById,
    pageLabel: (id) => pagesById.get(id)?.title || id,
    decisionLabel: (id) => decisionsById.get(id)?.title || id,
  }), [baseConfig, pagesById, decisionsById]);

  /* ── Graphe + layout ────────────────────────────────────────── */
  const graph = useMemo(() => {
    if (!config) return { nodes: [], edges: [], childrenOf: new Map(), parentOf: new Map() };
    return buildGraph(config, pages, decisions);
  }, [config, pages, decisions]);

  const usage = useMemo(() => {
    if (!allConfigs.length) return { pageUsage: new Map(), decisionUsage: new Map() };
    return computeSharedUsage(allConfigs, pages, decisions);
  }, [allConfigs, pages, decisions]);

  const findingsIndex = useMemo(() => {
    const byPage = new Map();
    const byDecision = new Map();
    (findings || []).forEach((f) => {
      if (f.page_id) {
        if (!byPage.has(f.page_id)) byPage.set(f.page_id, []);
        byPage.get(f.page_id).push(f);
      }
      if (f.decision_id) {
        if (!byDecision.has(f.decision_id)) byDecision.set(f.decision_id, []);
        byDecision.get(f.decision_id).push(f);
      }
    });
    return { byPage, byDecision };
  }, [findings]);

  const visibleGraph = useMemo(() => ({
    nodes: graph.nodes,
    edges: graph.edges.filter((e) => !hiddenKinds.has(e.kind)),
    childrenOf: graph.childrenOf,
    parentOf: graph.parentOf,
  }), [graph, hiddenKinds]);

  const layout = useMemo(
    () => layoutGraph(visibleGraph, { expandedCards, expandedGroups, manualPositions }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleGraph, expandedCards, expandedGroups, manualPositions, layoutNonce]
  );

  const layoutNodeById = useMemo(() => new Map(layout.nodes.map((n) => [n.id, n])), [layout.nodes]);

  /* ── Emphase au survol ──────────────────────────────────────────
     Volontairement NON modale : on épaissit la branche survolée et on
     entoure ses cibles, sans éteindre le reste du graphe. Rien à quitter,
     rien à restaurer — contrairement à un mode focus, qui coûtait plus
     qu'il ne rapportait une fois le tronc lisible. */
  const emphasis = useMemo(() => {
    if (!hoverRow) return null;
    const seedEdges = layout.edges.filter(
      (e) => e.source === hoverRow.nodeId && parseHandle(e.sourceHandle).decisionId === hoverRow.decisionId
    );
    const nodes = collectSubtree(seedEdges.map((e) => e.target), graph.childrenOf);
    const edgeIds = new Set(seedEdges.map((e) => e.id));
    layout.edges.forEach((e) => {
      if (nodes.has(e.source) && nodes.has(e.target)) edgeIds.add(e.id);
    });
    return { nodes, edges: edgeIds };
  }, [hoverRow, layout.edges, graph.childrenOf]);

  /* ── Données de nœud (référence stable → pas de re-render au survol) ── */
  const nodeDataById = useMemo(() => {
    const map = new Map();
    layout.nodes.forEach((n) => {
      if (n.kind === "group") { map.set(n.id, { node: n }); return; }
      const decisionFindings = {};
      const decisionUsage = {};
      (n.rows || []).forEach((r) => {
        const f = findingsIndex.byDecision.get(r.decisionId);
        if (f) decisionFindings[r.decisionId] = f;
        decisionUsage[r.decisionId] = Array.from(usage.decisionUsage.get(r.decisionId) || []);
      });
      map.set(n.id, {
        node: n,
        findings: { page: findingsIndex.byPage.get(n.pageId) || [], decisions: decisionFindings },
        usage: { page: Array.from(usage.pageUsage.get(n.pageId) || []), decisions: decisionUsage },
      });
    });
    return map;
  }, [layout.nodes, findingsIndex, usage]);

  const flowNodes = useMemo(() => layout.nodes.map((n) => ({
    id: n.id,
    type: n.kind === "group" ? "group" : "page",
    position: n.position,
    draggable: true,
    deletable: false,
    className: [
      emphasis?.nodes.has(n.id) ? "graph-node-emph" : "",
      pulseNodeId === n.id ? "graph-node-pulse" : "",
    ].filter(Boolean).join(" "),
    style: { width: n.width },
    data: nodeDataById.get(n.id),
  })), [layout.nodes, nodeDataById, emphasis, pulseNodeId]);

  const flowEdges = useMemo(() => layout.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle,
    targetHandle: e.targetHandle,
    type: e.kind === "sequence" ? "sequence" : "consequence",
    data: e,
    deletable: !!e.ref,
    className: emphasis?.edges.has(e.id) ? "graph-edge-emph" : "",
    markerEnd: { type: MarkerType.ArrowClosed, color: e.missing ? "#dc2626" : EDGE_COLORS[e.kind] },
    zIndex: e.conditional ? 2 : 1,
  })), [layout.edges, emphasis]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  useEffect(() => { setNodes(flowNodes); }, [flowNodes, setNodes]);
  useEffect(() => { setEdges(flowEdges); }, [flowEdges, setEdges]);

  /* ── Brouillon : application + historique ───────────────────── */
  const applyDraft = useCallback((fn) => {
    const prev = draftRef.current;
    const next = fn(prev);
    if (next === prev) return;
    historyRef.current = [...historyRef.current, prev].slice(-50);
    draftRef.current = next;
    setDraft(next);
  }, []);

  const undo = useCallback(() => {
    const prev = historyRef.current.pop();
    if (!prev) return;
    draftRef.current = prev;
    setDraft(prev);
  }, []);

  const discard = useCallback(() => {
    historyRef.current = [];
    draftRef.current = Draft.emptyDraft;
    setDraft(Draft.emptyDraft);
  }, []);

  const getConsequence = useCallback((ref) => {
    if (!ref) return null;
    if (ref.doc === "decision") return (decisionsById.get(ref.id)?.[ref.list] || [])[ref.index] || null;
    return (pagesById.get(ref.id)?.page_general_consequence || [])[ref.index] || null;
  }, [decisionsById, pagesById]);

  /* ── Actions du graphe ──────────────────────────────────────── */
  const pushDecisionInDrawer = useCallback((decId, label) => {
    setDrawerStack((prev) => [...prev, { type: "decision", id: decId, label: label || decId }]);
  }, []);

  const closeDrawer = useCallback(() => { setDrawerStack([]); reload(); }, [reload]);

  const revealNode = useCallback((nodeId) => {
    const n = layoutNodeById.get(nodeId);
    if (!n) return;
    setCenter(n.position.x + (n.width || NODE_W) / 2, n.position.y + (n.height || 80) / 2, { zoom: 1, duration: 500 });
    setPulseNodeId(nodeId);
    setTimeout(() => setPulseNodeId((cur) => (cur === nodeId ? null : cur)), 2000);
  }, [layoutNodeById, setCenter]);

  const openConditionPopover = useCallback((edgeLike, anchor) => {
    if (!edgeLike.ref) return;
    const consequence = getConsequence(edgeLike.ref);
    // Référence périmée (le document a changé sous nos pieds) : mieux vaut ne
    // rien ouvrir que d'éditer un index qui ne pointe plus au bon endroit.
    if (!consequence) return;
    setPopover({
      kind: "condition",
      anchor,
      edge: {
        ...edgeLike,
        consequence,
        targetPageId: edgeLike.targetPageId
          || layoutNodeById.get(edgeLike.target)?.pageId
          || graph.nodes.find((n) => n.id === edgeLike.target)?.pageId,
      },
    });
  }, [getConsequence, layoutNodeById, graph.nodes]);

  const actions = useMemo(() => ({
    onOpenPage: setComposerPageId,
    onOpenDecision: pushDecisionInDrawer,
    onHoverRow: (nodeId, decisionId) => setHoverRow(nodeId ? { nodeId, decisionId } : null),
    onToggleCard: (nodeId) => setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
      return next;
    }),
    onToggleGroup: (groupId) => setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      return next;
    }),
    onInsertAt: (index, anchor) => setPopover({
      kind: "pick-page",
      title: `Insérer une étape en position ${index + 1}`,
      anchor,
      apply: (d, pageId) => Draft.insertPileStep(d, base, index, pageId),
    }),
    onRemoveFromPile: (pileIndex, pageId) => {
      const label = pagesById.get(pageId)?.title || pageId;
      // Retirer une étape déplace tout le reste de la pile : on confirme.
      if (!window.confirm(`Retirer « ${label} » de la pile de cet atelier ?\n\nLa page elle-même n'est pas supprimée, et les autres ateliers ne sont pas touchés.`)) return;
      applyDraft((d) => Draft.removePileStep(d, base, pileIndex));
    },
    onEditEdge: openConditionPopover,
    onEditConsequence: openConditionPopover,
  }), [pushDecisionInDrawer, applyDraft, base, pagesById, openConditionPopover]);

  /* ── Connexions : créer une conséquence par glisser ──────────── */
  const refForHandle = useCallback((sourceNodeId, sourceHandle) => {
    const sourceNode = graph.nodes.find((n) => n.id === sourceNodeId);
    if (!sourceNode) return null;
    const parsed = parseHandle(sourceHandle);
    if (parsed.kind === "decision") {
      return {
        doc: "decision",
        id: parsed.decisionId,
        list: parsed.side === "taken" ? "add_elem_to_pile_if_taken" : "add_elem_to_pile_if_not_taken",
      };
    }
    return { doc: "page", id: sourceNode.pageId, list: "page_general_consequence" };
  }, [graph.nodes]);

  const onConnect = useCallback((connection) => {
    // Le port bas du tronc sert à lire l'ordre de la pile, pas à créer un lien.
    if (connection.sourceHandle === SEQ_SOURCE_HANDLE) return;
    const ref = refForHandle(connection.source, connection.sourceHandle);
    const targetNode = graph.nodes.find((n) => n.id === connection.target) || layoutNodeById.get(connection.target);
    if (!ref || !targetNode?.pageId) return;
    applyDraft((d) => Draft.addConsequence(d, base, ref, targetNode.pageId));
  }, [refForHandle, graph.nodes, layoutNodeById, applyDraft, base]);

  const onConnectEnd = useCallback((event, connectionState) => {
    if (connectionState?.isValid) return;             // déjà traité par onConnect
    const fromNodeId = connectionState?.fromNode?.id;
    const fromHandle = connectionState?.fromHandle?.id;
    if (!fromNodeId || fromHandle === SEQ_SOURCE_HANDLE) return;
    const ref = refForHandle(fromNodeId, fromHandle);
    if (!ref) return;

    const point = "changedTouches" in event ? event.changedTouches[0] : event;
    setPopover({
      kind: "pick-page",
      title: "Ajouter une conséquence",
      anchor: { x: point.clientX, y: point.clientY },
      apply: (d, pageId) => Draft.addConsequence(d, base, ref, pageId),
    });
  }, [refForHandle, base]);

  const onEdgesDelete = useCallback((deleted) => {
    deleted.forEach((e) => {
      if (!e.data?.ref) return;
      applyDraft((d) => Draft.removeConsequence(d, base, e.data.ref));
    });
  }, [applyDraft, base]);

  /* ── Déplacement : le tronc se réordonne, le reste se positionne ── */
  const onNodeDragStop = useCallback((event, node) => {
    const meta = layoutNodeById.get(node.id);
    if (!meta) return;

    if (meta.inPile) {
      const others = layout.nodes
        .filter((n) => n.inPile && n.id !== node.id)
        .sort((a, b) => a.autoPosition.y - b.autoPosition.y);
      let index = others.findIndex((o) => node.position.y < o.autoPosition.y + o.height / 2);
      if (index === -1) index = others.length;
      if (index !== meta.pileIndex) {
        applyDraft((d) => Draft.reorderPile(d, base, meta.pileIndex, index));
      }
      // Le tronc est une colonne : on le remet toujours en place.
      setLayoutNonce((n) => n + 1);
      setNodes((ns) => ns.map((n) => (n.id === node.id ? { ...n, position: meta.autoPosition } : n)));
      return;
    }

    setManualPositions((prev) => ({ ...prev, [node.id]: node.position }));
  }, [layoutNodeById, layout.nodes, applyDraft, base, setNodes]);

  /* ── Validateur ─────────────────────────────────────────────── */
  const runValidation = useCallback(async () => {
    setValidating(true);
    try {
      const res = await apiGet(`/validate_game_config/${configId}`);
      setFindings(res.findings || []);
    } finally {
      setValidating(false);
    }
  }, [apiGet, configId]);

  /* ── Sauvegarde ─────────────────────────────────────────────── */
  const affectedConfigs = useMemo(() => {
    const set = new Set();
    Object.keys(draft.pages).forEach((id) => (usage.pageUsage.get(id) || new Set()).forEach((c) => set.add(c)));
    Object.keys(draft.decisions).forEach((id) => (usage.decisionUsage.get(id) || new Set()).forEach((c) => set.add(c)));
    set.delete(configId);
    return Array.from(set);
  }, [draft, usage, configId]);

  const doSave = useCallback(async (force) => {
    setSaving(true);
    try {
      const result = await Draft.saveDraft(draftRef.current, { apiUrl, token, force });
      if (result.ok) {
        setSaveState(null);
        discard();
        await reload();
        if (findings) await runValidation();
        setToast("Modifications enregistrées.");
      } else if (result.blocked) {
        setSaveState({ blocked: true, entity: result.entity, id: result.id, result: result.result });
      } else {
        setSaveState({ error: result.result?.message || "Erreur inconnue lors de la sauvegarde." });
      }
    } catch (e) {
      setSaveState({ error: e.message });
    } finally {
      setSaving(false);
    }
  }, [apiUrl, token, discard, reload, findings, runValidation]);

  /* ── Raccourcis clavier ─────────────────────────────────────── */
  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(e.target?.tagName);
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && !typing) {
        e.preventDefault();
        undo();
      } else if (e.key === "Escape" && !typing) {
        setPopover(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const toggleKind = (kind) => setHiddenKinds((prev) => {
    const next = new Set(prev);
    if (next.has(kind)) next.delete(kind); else next.add(kind);
    return next;
  });

  const expandableIds = useMemo(
    () => layout.nodes.filter((n) => n.kind !== "group" && isExpandable(n)).map((n) => n.id),
    [layout.nodes]
  );
  const allGroupIds = useMemo(
    () => layout.nodes.filter((n) => n.kind === "group").map((n) => n.id),
    [layout.nodes]
  );

  const goBack = () => navigate(`/builder/config/${configId}`);

  if (loading || !config) return <div className="builder-loading">Chargement du graphe…</div>;

  const dirty = Draft.isDirty(draft);
  const errorCount = (findings || []).filter((f) => f.severity === "error").length;
  const warningCount = (findings || []).filter((f) => f.severity === "warning").length;

  return (
    <GraphActions.Provider value={actions}>
      <div className="graph-page">
        <div className="graph-header">
          <div className="graph-header-left">
            <button className="builder-back-btn" onClick={goBack}>← Retour à l'atelier</button>
            <h1 className="graph-header-title">{config.name || config._id}</h1>
          </div>

          <div className="graph-toolbar">
            <button className="graph-tool-btn" onClick={() => setPaletteOpen(true)} title="Rechercher (Cmd/Ctrl + K)">🔍 Rechercher</button>
            <button
              className="graph-tool-btn"
              onClick={() => setExpandedCards((prev) => (prev.size ? new Set() : new Set(expandableIds)))}
              title="Déplier / replier toutes les cartes de décisions"
            >
              {expandedCards.size ? "⤡ Replier" : "⤢ Déplier"} les mesures
            </button>
            <button
              className="graph-tool-btn"
              onClick={() => setExpandedGroups((prev) => (prev.size ? new Set() : new Set(allGroupIds)))}
              disabled={!allGroupIds.length}
              title="Déplier / replier les groupes de conséquences"
            >
              {expandedGroups.size ? "⊟" : "⊞"} Groupes
            </button>
            <button
              className="graph-tool-btn"
              onClick={() => { setManualPositions({}); setLayoutNonce((n) => n + 1); }}
              title="Revenir au placement automatique"
            >⟲ Replacer</button>
            <button className="graph-tool-btn" onClick={runValidation} disabled={validating}>
              {validating ? "Vérification…" : "🔍 Vérifier"}
            </button>
            {findings && (
              <span className="graph-validation-summary">
                <span className="sev-error">⛔ {errorCount}</span>
                <span className="sev-warning">⚠ {warningCount}</span>
              </span>
            )}
          </div>

          <div className="graph-legend">
            {EDGE_LEGEND.map((l) => (
              <button
                key={l.kind}
                className={`graph-legend-item ${hiddenKinds.has(l.kind) ? "off" : ""}`}
                onClick={() => toggleKind(l.kind)}
                title="Cliquer pour afficher/masquer ce type de lien"
              >
                <span className="graph-legend-swatch" style={{ background: EDGE_COLORS[l.kind] }} />
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <div className="graph-canvas">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectEnd={onConnectEnd}
            onEdgesDelete={onEdgesDelete}
            onNodeDragStop={onNodeDragStop}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            nodesDeletable={false}
            /* Pas de Backspace : il supprimerait une arête sélectionnée pendant
               qu'on tape dans un popover. */
            deleteKeyCode={["Delete"]}
            fitView
            minZoom={0.08}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={26} />
            <Controls />
            <MiniMap pannable zoomable nodeStrokeWidth={2} />
          </ReactFlow>

          {findings && (
            <FindingsPanel
              findings={findings}
              dirty={dirty}
              onFocusPage={(pageId) => {
                const target = layout.nodes.find((n) => n.pageId === pageId);
                if (target) revealNode(target.id);
              }}
              onClose={() => setFindings(null)}
            />
          )}
        </div>

        {dirty && (
          <ChangesBar
            changes={draft.changes}
            saving={saving}
            onUndo={undo}
            onDiscard={discard}
            onSave={() => setSaveState({ affectedConfigs })}
          />
        )}

        {toast && <div className="graph-toast">{toast}</div>}

        {popover?.kind === "pick-page" && (
          <PagePickerPopover
            anchor={popover.anchor}
            title={popover.title}
            pages={pages}
            existingIds={new Set(pages.map((p) => p._id))}
            onPick={(pageId) => { applyDraft((d) => popover.apply(d, pageId)); setPopover(null); }}
            onCreate={(page) => {
              // La page est créée dans le brouillon puis immédiatement reliée :
              // les deux gestes n'en font qu'un pour l'auteur.
              applyDraft((d) => popover.apply(Draft.createPage(d, page), page._id));
              setPopover(null);
            }}
            onClose={() => setPopover(null)}
          />
        )}

        {popover?.kind === "condition" && (
          <ConditionPopover
            anchor={popover.anchor}
            edge={popover.edge}
            pages={pages}
            decisions={decisions}
            scoreDic={config.score_dic}
            configId={config._id}
            onApply={(patch) => {
              applyDraft((d) => Draft.updateConsequence(d, base, popover.edge.ref, patch, config._id));
              setPopover(null);
            }}
            onDelete={() => {
              applyDraft((d) => Draft.removeConsequence(d, base, popover.edge.ref));
              setPopover(null);
            }}
            onClose={() => setPopover(null)}
          />
        )}

        {paletteOpen && (
          <CommandPalette
            nodes={layout.nodes.filter((n) => n.kind !== "group")}
            decisions={decisions}
            onSelectNode={(nodeId) => { setPaletteOpen(false); revealNode(nodeId); }}
            onSelectDecision={(decId) => {
              setPaletteOpen(false);
              const host = layout.nodes.find((n) => (n.rows || []).some((r) => r.decisionId === decId));
              if (host) revealNode(host.id);
              else pushDecisionInDrawer(decId);
            }}
            onClose={() => setPaletteOpen(false)}
          />
        )}

        <SaveDialog
          state={saveState}
          onConfirm={() => doSave(false)}
          onForce={() => doSave(true)}
          onClose={() => setSaveState(null)}
        />

        {/* Ouvrir une page depuis le graphe, c'est la voir ET la modifier :
            le composeur se pose par-dessus le graphe, qui garde son brouillon. */}
        {composerPageId && (
          <PageComposer
            pageId={composerPageId}
            config={config}
            onClose={() => { setComposerPageId(null); reload(); }}
            onSaved={reload}
            onNavigate={setComposerPageId}
            onRemoveFromPile={(pageId) => {
              const pileIndex = (base.config.pile || []).indexOf(pageId);
              if (pileIndex >= 0) applyDraft((d) => Draft.removePileStep(d, base, pileIndex));
              setComposerPageId(null);
            }}
          />
        )}

        {drawerStack.length > 0 && (
          <SideDrawer stack={drawerStack} onClose={closeDrawer} onPop={() => setDrawerStack((p) => p.slice(0, -1))}>
            {drawerStack.map((item, i) => (
              <div key={`${item.type}-${item.id}`} style={{ display: i === drawerStack.length - 1 ? "block" : "none", height: "100%" }}>
                <DecisionEditor
                  embeddedId={item.id}
                  onClose={() => setDrawerStack((p) => p.slice(0, -1))}
                  parentConfig={config}
                />
              </div>
            ))}
          </SideDrawer>
        )}
      </div>
    </GraphActions.Provider>
  );
}

export default function WorkshopGraph() {
  return (
    <ReactFlowProvider>
      <WorkshopGraphInner />
    </ReactFlowProvider>
  );
}
